import { WeatherConditions } from "../types";
import { API_CONFIG } from "../config/api";

const WEATHER_API_KEY = API_CONFIG.weather.apiKey;
const WEATHER_API_BASE = API_CONFIG.weather.baseUrl;

export function unavailableWeather(): WeatherConditions {
  return { available: false, source: 'unavailable' };
}

export const getCurrentWeather = async (lat?: number, lon?: number, autoLocation: boolean = false): Promise<WeatherConditions> => {
  try {
    let query: string | null = null;

    if (lat != null && lon != null) {
      query = `${lat},${lon}`;
    } else if (autoLocation && typeof navigator !== 'undefined' && navigator.geolocation) {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          timeout: 5000,
          enableHighAccuracy: false
        });
      }).catch(() => null);

      if (position) {
        query = `${position.coords.latitude},${position.coords.longitude}`;
      }
    }

    if (!query || !WEATHER_API_KEY) {
      return unavailableWeather();
    }

    const response = await fetch(
      `${WEATHER_API_BASE}/current.json?key=${WEATHER_API_KEY}&q=${query}&aqi=no`
    );

    if (!response.ok) {
      return unavailableWeather();
    }

    const data = await response.json();
    return {
      available: true,
      source: 'live',
      temperature: Math.round(data.current.temp_f),
      windSpeed: Math.round(data.current.wind_mph),
      windDirection: data.current.wind_dir,
      humidity: data.current.humidity,
      pressure: data.current.pressure_in,
    };
  } catch {
    return unavailableWeather();
  }
};

export const getWeatherAdjustment = (
  baseDistance: number,
  weather: WeatherConditions,
  elevation: number = 0,
): number => {
  if (!weather.available || weather.temperature == null || weather.windSpeed == null || weather.humidity == null) {
    return baseDistance;
  }

  let adjustedDistance = baseDistance;
  const tempAdjustment = (weather.temperature - 70) * 0.1;
  adjustedDistance += tempAdjustment;
  adjustedDistance -= weather.windSpeed * 0.5;
  const altitudeAdjustment = (elevation / 1000) * 0.02 * baseDistance;
  adjustedDistance += altitudeAdjustment;
  adjustedDistance += (weather.humidity - 50) * 0.01;
  return Math.round(adjustedDistance);
};

export const getWeatherRecommendations = (
  weather: WeatherConditions,
): string[] => {
  if (!weather.available || weather.temperature == null || weather.windSpeed == null || weather.humidity == null || weather.pressure == null) {
    return [];
  }

  const recommendations: string[] = [];

  if (weather.temperature < 50) {
    recommendations.push("Cold weather: Ball won't carry as far, take one more club");
    recommendations.push("Keep hands warm between shots for better grip");
  } else if (weather.temperature > 85) {
    recommendations.push("Hot weather: Ball will carry farther, consider one less club");
    recommendations.push("Stay hydrated and take breaks in shade");
  }

  if (weather.windSpeed > 15) {
    recommendations.push("Strong wind: Take more club and swing easier for control");
    recommendations.push("Keep ball flight lower in windy conditions");
  } else if (weather.windSpeed > 8) {
    recommendations.push("Moderate wind: Factor in wind direction for club selection");
  }

  if (weather.humidity > 80) {
    recommendations.push("High humidity: Grips may be slippery, consider using a towel");
    recommendations.push("Ball may not carry as far in humid conditions");
  }

  if (weather.pressure < 29.8) {
    recommendations.push("Low pressure: Ball may not carry as far");
  } else if (weather.pressure > 30.2) {
    recommendations.push("High pressure: Ball may carry slightly farther");
  }

  return recommendations;
};

export const getUserLocation = (): Promise<{lat: number, lon: number}> => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by this browser'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lon: position.coords.longitude
        });
      },
      (error) => {
        reject(error);
      },
      {
        timeout: 10000,
        enableHighAccuracy: true
      }
    );
  });
};
